// node_modules
import React, { useContext, useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { ToastContext } from "../../App";

// hooks
import { useWS } from "../../hooks/useWS";
import { useGetCustomerMeQuery } from "../../redux/services/customer";
import { diningOptionApi } from "../../redux/services/diningOption";
import { foodStationOptionsApi } from "../../redux/services/foodStationOptions";
import { organizationApi } from "../../redux/services/organization";

// redux
import { setOpenedStations } from "../../redux/slices/stations.slice";
import { useSelector, useDispatch } from "react-redux";
import {
  setVapidKey,
  setLoading,
  setTimezone,
  setNearestSlot,
  setUpdateStations,
} from "../../redux/slices/app.slice";
import { setOrders } from "../../redux/slices/order.slice";
import getBaseUrl from "../../utils/getBaseUrl";

// components
import StudentHome from "./StudentHome";
import OrderPage from "./OrderPage/OrderPage";
import OrderStatusPage from "./OrderStatusPage";
import Account from "./Account/Account";
import Settings from "./Settings/Settings";
import Footer from "../../components/Footer";
import ForklyWidget from "../../components/ForklyWidget/ForklyWidget";

// assets
import BackgroundEllipse from "../../assets/closed_stations_ellipse.svg";

// utils
import getSubdomain from "../../utils/getSubdomain";
import Root from "../Root";

// styles
import * as styles from "./StudentPortal.module.css";
import { foodStationApi } from "../../redux/services/foodStation";

const StudentPortal = () => {
  const [showLoader, setShowLoader] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAccountOrSettings =
    location.pathname === "/account" || location.pathname === "/settings";
  const reduxAccessToken = useSelector((s) => s.user.access_token);
  const { data: customer } = useGetCustomerMeQuery(undefined, {
    skip: !localStorage.getItem("access_token"),
  });
  const chatbotAllowedEmail = process.env.CHATBOT_ALLOWED_EMAIL;
  const isChatbotAllowed =
    !chatbotAllowedEmail || customer?.email === chatbotAllowedEmail;
  const { ws } = useWS({ token: reduxAccessToken || localStorage.getItem("access_token") });
  const isStudentLoggedIn = Boolean(
    reduxAccessToken || localStorage.getItem("access_token"),
  );
  const subdomain = getSubdomain();
  const isMainDomain =
    subdomain.startsWith("localhost") ||
    subdomain === "dwtl" ||
    subdomain === "deltaspark";

  useEffect(() => {
    if (!isMainDomain && !isStudentLoggedIn) {
      navigate("/student/login");
    }
  }, [isMainDomain, isStudentLoggedIn, navigate]);

  useEffect(() => {
    if (isAccountOrSettings) {
      document.body.classList.add("accountSettingsBg");
    } else {
      document.body.classList.remove("accountSettingsBg");
    }
    return () => {
      document.body.classList.remove("accountSettingsBg");
    };
  }, [isAccountOrSettings]);
  const [stationsChanged, setStationsChanged] = useState(false);
  const showToast = useContext(ToastContext);
  const openedStations = useSelector((state) => state.stations.openedStations);
  const updateStations = useSelector((state) => state.app.updateStations);

  useEffect(() => {
    if (isMainDomain) {
      dispatch(setLoading(false));
    } else {
      dispatch(setLoading(true));
    }
  }, [dispatch, isMainDomain]);

  useEffect(() => {
    if (isMainDomain) return;
    if (!ws) return;
    ws.addEventListener("open", (event) => {
      console.log("Open!", event);
      setInterval(() => {
        ws.send(JSON.stringify({ type: "ping" }));
      }, 1000 * 55);
      ws.addEventListener("message", (event) => {
        const data = JSON.parse(event.data);

        if (data.status === "success") {
          ws.send(JSON.stringify({ type: "current-stations", data: { subdomain: getSubdomain() } }));
          ws.send(JSON.stringify({ type: "timezone" }));
          ws.send(JSON.stringify({ type: "nearest-slot" }));
          ws.send(JSON.stringify({ type: "order-status", data: {} }));
        }

        if (data.type === "timezone") {
          dispatch(setTimezone(data.data.timezone));
        }

        if (data.type === "nearest-slot") {
          dispatch(setNearestSlot(data.data.nearest_slot));
        }

        if (data.type === "update-available-stations") {
          dispatch(foodStationApi.util.invalidateTags([{ type: "Stations" }]));

          ws.send(JSON.stringify({ type: "current-stations", data: { subdomain: getSubdomain() } }));
          ws.send(JSON.stringify({ type: "nearest-slot" }));
        }

        if (data.type === "available-stations") {
          dispatch(setOpenedStations(data.data));
          dispatch(setLoading(false));
          if (updateStations) dispatch(setUpdateStations(false));
        }

        if (data.type === "order-status") {
          dispatch(setOrders(data.data));
        }

        if (
          data.type === "new-order" ||
          data.type === "update-order" ||
          data.type === "delete-order"
        ) {
          ws.send(JSON.stringify({ type: "order-status", data: {} }));
        }

        if (data.type === "organization-changed") {
          if (data.data && data.data === "Subdomain changed") {
            showToast("info", "Organization", "Organization subdomain changed");
            if (window.location.protocol === "https:")
              window.location.href = `https://${getBaseUrl()}`;
            else window.location.href = `http://${getBaseUrl()}`;
          } else if (data.data && data.data === "Organization deleted") {
            showToast("warn", "Organization", "Organization deleted");
            if (window.location.protocol === "https:")
              window.location.href = `https://${getBaseUrl()}`;
            else window.location.href = `http://${getBaseUrl()}`;
          } else {
            dispatch(
              organizationApi.util.invalidateTags([
                { type: "NotificationOptions" },
              ]),
            );
            showToast(
              "info",
              "Organization",
              "Please check out the new notification options!",
            );
          }
        }

        if (data.type === "station-changed") {
          if (
            +window.location.href.slice(
              window.location.href.indexOf("make-order/") + 11,
            ) === data.data.station_id
          ) {
            dispatch(
              diningOptionApi.util.invalidateTags([{ type: "DiningOptions" }]),
            );
            dispatch(
              foodStationOptionsApi.util.invalidateTags([
                { type: "FoodStationOptions" },
              ]),
            );
            showToast(
              "info",
              "The Food Station refreshed!",
              "Please check out the new options!",
            );
            setStationsChanged(true);
          } else {
            ws.send(JSON.stringify({ type: "current-stations", data: { subdomain: getSubdomain() } }));
          }
        }

        if (data.key) {
          dispatch(setVapidKey(data.key));
        }
      });
    });

    ws.addEventListener("error", (event) => {
      dispatch(setLoading(false));
      console.log("Error!", event);
    });

    ws.addEventListener("close", (event) => {
      dispatch(setLoading(false));
      navigate(0);
      console.log("Close!", event);
    });
  }, [ws, isMainDomain]);

  useEffect(() => {
    if (updateStations && ws) {
      ws.send(JSON.stringify({ type: "current-stations", data: { subdomain: getSubdomain() } }));
      ws.send(
        JSON.stringify({
          type: "nearest-slot",
        }),
      );
    }
  }, [updateStations, ws]);

  const sendViaWS = (message) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ ...message }));
    }
  };

  return (
    <div
      style={{
        background: `${
          isAccountOrSettings
            ? "transparent"
            : openedStations?.length === 0
            ? `url(${BackgroundEllipse}) right / 50% 100% no-repeat, linear-gradient(#F5F2ED, #FBFAF9)`
            : ""
        } `,
      }}
      className={`${styles.wrapper} ${
        openedStations?.length === 0 && !isAccountOrSettings
          ? styles.closedPage
          : ""
      }`}
    >
      {isMainDomain ? (
        <Root />
      ) : (
        <div>
          <Routes>
            <Route path="/" element={<StudentHome sendViaWS={sendViaWS} />} />
            <Route
              path="/make-order/:id"
              element={
                <OrderPage
                  sendViaWS={sendViaWS}
                  stationChanged={stationsChanged}
                  setStationChanged={setStationsChanged}
                />
              }
            />
            <Route
              path="/order-status"
              element={<OrderStatusPage sendViaWS={sendViaWS} />}
            />
            <Route path="/account" element={<Account />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </div>
      )}
      <Footer />
      {isChatbotAllowed && <ForklyWidget sendViaWS={sendViaWS} />}
    </div>
  );
};

export default StudentPortal;
